import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Small line above the title. */
  eyebrow?: string;
  /** Accessible name of the close button. */
  closeLabel: string;
  /** Extra header controls, shown before the close button (e.g. a manual download). */
  actions?: ReactNode;
  /** Paper width per breakpoint; the default suits a text-only help. */
  width?: Record<string, string | number>;
  children: ReactNode;
}

const DEFAULT_WIDTH = { xs: "100%", sm: 460 };

/** Right-hand help drawer: sticky header with title and close button, scrolling body. */
export function HelpDrawer({ open, onClose, title, eyebrow, closeLabel, actions, width = DEFAULT_WIDTH, children }: Props) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlideProps={{ appear: true }}
      PaperProps={{ sx: { width, maxWidth: "100%" } }}
      aria-label={title}
    >
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          py: 1.75,
          pl: 2.5,
          pr: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <HelpOutlineIcon aria-hidden sx={{ color: "text.secondary" }} />
        <Box sx={{ flex: 1 }}>
          {eyebrow && (
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
              {eyebrow}
            </Typography>
          )}
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {title}
          </Typography>
        </Box>
        {actions}
        <IconButton onClick={onClose} aria-label={closeLabel} sx={{ border: 1, borderColor: "divider" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: 2.5, pt: 0.75, pb: 3.5, overflowY: "auto", "& p": { m: "0 0 10px" } }}>{children}</Box>
    </Drawer>
  );
}
